
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { FieldInput, FieldMeta } from '@pinto/react-form';

import OptionType from 'src/propTypes/SelectOptionType';
import { SelectOption, SelectOptionKey } from 'src/typeDefs';

interface SelectOptionWithId extends SelectOption {
    id: string;
}

export type SelectProps = {
    value?: SelectOptionKey;
    onChange: (value: SelectOptionKey, option?: SelectOption) => any,
    /**
     * List of valid select options
     * String options will be converted into { key: value, label: value }
     */
    options: string[] | SelectOption[];
    /**
     * Label for the entire Select component, left aligned
     */
    label?: string | JSX.Element;
    /**
     * Disable groups even though they are part of options
     */
    noGroups?: boolean;
    /**
     * The label for the "no option selected" option.
     * Defaults to "[ select ]"
     * Also the label that shows up when the value is not found in options
     */
    noOptionLabel?: boolean;
}

export interface SelectFieldProps extends Omit<SelectProps, 'onChange' | 'value'> {
    input: FieldInput;
    meta: FieldMeta;
    [key: string]: any;
};

export class Select extends React.PureComponent<SelectProps & { [key: string]: any; }> {
    static propTypes = {
        value: PropTypes.string,
        label: PropTypes.node,
        onChange: PropTypes.func.isRequired,
        options: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.string,
                OptionType,
            ]),
        ).isRequired,
        noGroups: PropTypes.bool,
        noOptionLabel: PropTypes.string.isRequired,
    }

    static defaultProps = {
        noOptionLabel: '[ select ]',
    }

    handleChange = ({ target: { value } }: any) => {
        const match = _.find(this.getOptions(), { id: value }) as SelectOption;
        if (match) this.props.onChange(match.key, match);
    }

    getOptionsMemoized = memoizeOne((value, noOptionLabel, options: SelectProps['options']): SelectOptionWithId[] => {
        // @ts-ignore
        const finalOptions: SelectOptionWithId[] = options.map(item =>
            typeof item === 'string'
                ? { key: item, label: item, group: null }
                : { ...item, group: item.group == null ? null : item.group }
        );

        if (!_.find(finalOptions, { key: value })) {
            finalOptions.unshift({ id: value, key: value, label: noOptionLabel, group: undefined });
            value = null;
        }

        for (const [index, opt] of finalOptions.entries()) opt.id = String(index);

        return finalOptions;
    })

    getOptions() {
        return this.getOptionsMemoized(
            this.props.value,
            this.props.noOptionLabel,
            this.props.options
        );
    }

    getGroups = memoizeOne((noGroups, finalOptions: SelectOptionWithId[]) => {
        let groups = [{ label: null, list: finalOptions }];

        if (!noGroups && finalOptions.some(opt => opt.group)) {
            const map = new Map();
            let order = [];
            for (const opt of finalOptions) {
                map.set(opt.group, [...(map.get(opt.group) || []), opt]);
                order.push(opt.group);
            }
            order = _.uniq(order);
            order = [null, ..._.sortBy(_.filter(order))];
            groups = (order as any[]).map(label => ({ label, list: map.get(label) || [] }));
        }

        return groups;
    })

    render() {
        const {
            value,
            onChange,
            label,
            noGroups,
            noOptionLabel,
            options,
            disabled,
            ...rest
        } = this.props;
        const finalOptions = this.getOptions();
        const groups = this.getGroups(noGroups, finalOptions);

        return (
            <Root tabIndex='0' {...rest} Tag='label'>
                {label && <Label>{label}</Label>}
                <Input
                    Tag='select'
                    tabIndex='-1'
                    onChange={this.handleChange}
                    disabled={disabled}
                    value={_.get(_.find(finalOptions, { key: value }), 'id', '')}
                >
                    {groups.map(({ label, list }) => {
                        const optList = list.map(({ key, label, id }) =>
                            <option key={id} value={id}>{label}</option>
                        );

                        return label
                            ? <optgroup key={label} label={label as any}>{optList}</optgroup>
                            : <React.Fragment key={label}>{optList}</React.Fragment>;
                    })}
                </Input>
            </Root>
        );
    }
}

export const SelectField: React.FC<SelectFieldProps> = ({ input: { value, onChange }, meta, ...rest }) =>
    <Select
        {...rest}
        value={value === '' ? null : value}
        onChange={onChange}
    />;

export default Select;


require('!style-loader/lib/addStyles.js')([[ module.id, ".flex {\n  display: flex;\n}\n.flex.flex-space-between {\n  align-items: space-between;\n}\n.center {\n  text-align: center;\n}\n.bold {\n  font-weight: bold;\n}\n.mt0 {\n  margin-top: 0rem !important;\n}\n.mb0 {\n  margin-bottom: 0rem !important;\n}\n.ml0 {\n  margin-left: 0rem !important;\n}\n.mr0 {\n  margin-right: 0rem !important;\n}\n.m0 {\n  margin: 0rem 0rem 0rem 0rem !important;\n}\n.mx0 {\n  margin-left: 0rem !important;\n  margin-right: 0rem !important;\n}\n.my0 {\n  margin-top: 0rem !important;\n  margin-bottom: 0rem !important;\n}\n.mt1 {\n  margin-top: 1rem !important;\n}\n.mb1 {\n  margin-bottom: 1rem !important;\n}\n.ml1 {\n  margin-left: 1rem !important;\n}\n.mr1 {\n  margin-right: 1rem !important;\n}\n.m1 {\n  margin: 1rem 1rem 1rem 1rem !important;\n}\n.mx1 {\n  margin-left: 1rem !important;\n  margin-right: 1rem !important;\n}\n.my1 {\n  margin-top: 1rem !important;\n  margin-bottom: 1rem !important;\n}\n.mt2 {\n  margin-top: 2rem !important;\n}\n.mb2 {\n  margin-bottom: 2rem !important;\n}\n.ml2 {\n  margin-left: 2rem !important;\n}\n.mr2 {\n  margin-right: 2rem !important;\n}\n.m2 {\n  margin: 2rem 2rem 2rem 2rem !important;\n}\n.mx2 {\n  margin-left: 2rem !important;\n  margin-right: 2rem !important;\n}\n.my2 {\n  margin-top: 2rem !important;\n  margin-bottom: 2rem !important;\n}\n.mt05 {\n  margin-top: 0.5rem !important;\n}\n.mb05 {\n  margin-bottom: 0.5rem !important;\n}\n.ml05 {\n  margin-left: 0.5rem !important;\n}\n.mr05 {\n  margin-right: 0.5rem !important;\n}\n.m05 {\n  margin: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.mx05 {\n  margin-left: 0.5rem !important;\n  margin-right: 0.5rem !important;\n}\n.my05 {\n  margin-top: 0.5rem !important;\n  margin-bottom: 0.5rem !important;\n}\n.mt025 {\n  margin-top: 0.25rem !important;\n}\n.mb025 {\n  margin-bottom: 0.25rem !important;\n}\n.ml025 {\n  margin-left: 0.25rem !important;\n}\n.mr025 {\n  margin-right: 0.25rem !important;\n}\n.m025 {\n  margin: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.mx025 {\n  margin-left: 0.25rem !important;\n  margin-right: 0.25rem !important;\n}\n.my025 {\n  margin-top: 0.25rem !important;\n  margin-bottom: 0.25rem !important;\n}\n.pt0 {\n  padding-top: 0rem !important;\n}\n.pb0 {\n  padding-bottom: 0rem !important;\n}\n.pl0 {\n  padding-left: 0rem !important;\n}\n.pr0 {\n  padding-right: 0rem !important;\n}\n.p0 {\n  padding: 0rem 0rem 0rem 0rem !important;\n}\n.px0 {\n  padding-left: 0rem !important;\n  padding-right: 0rem !important;\n}\n.py0 {\n  padding-top: 0rem !important;\n  padding-bottom: 0rem !important;\n}\n.pt1 {\n  padding-top: 1rem !important;\n}\n.pb1 {\n  padding-bottom: 1rem !important;\n}\n.pl1 {\n  padding-left: 1rem !important;\n}\n.pr1 {\n  padding-right: 1rem !important;\n}\n.p1 {\n  padding: 1rem 1rem 1rem 1rem !important;\n}\n.px1 {\n  padding-left: 1rem !important;\n  padding-right: 1rem !important;\n}\n.py1 {\n  padding-top: 1rem !important;\n  padding-bottom: 1rem !important;\n}\n.pt2 {\n  padding-top: 2rem !important;\n}\n.pb2 {\n  padding-bottom: 2rem !important;\n}\n.pl2 {\n  padding-left: 2rem !important;\n}\n.pr2 {\n  padding-right: 2rem !important;\n}\n.p2 {\n  padding: 2rem 2rem 2rem 2rem !important;\n}\n.px2 {\n  padding-left: 2rem !important;\n  padding-right: 2rem !important;\n}\n.py2 {\n  padding-top: 2rem !important;\n  padding-bottom: 2rem !important;\n}\n.pt05 {\n  padding-top: 0.5rem !important;\n}\n.pb05 {\n  padding-bottom: 0.5rem !important;\n}\n.pl05 {\n  padding-left: 0.5rem !important;\n}\n.pr05 {\n  padding-right: 0.5rem !important;\n}\n.p05 {\n  padding: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.px05 {\n  padding-left: 0.5rem !important;\n  padding-right: 0.5rem !important;\n}\n.py05 {\n  padding-top: 0.5rem !important;\n  padding-bottom: 0.5rem !important;\n}\n.pt025 {\n  padding-top: 0.25rem !important;\n}\n.pb025 {\n  padding-bottom: 0.25rem !important;\n}\n.pl025 {\n  padding-left: 0.25rem !important;\n}\n.pr025 {\n  padding-right: 0.25rem !important;\n}\n.p025 {\n  padding: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.px025 {\n  padding-left: 0.25rem !important;\n  padding-right: 0.25rem !important;\n}\n.py025 {\n  padding-top: 0.25rem !important;\n  padding-bottom: 0.25rem !important;\n}\n.font-small-1 {\n  font-size: 0.857142857142857rem;\n}\n.font-large-1 {\n  font-size: 1.142857142857143rem;\n}\n.font-small-2 {\n  font-size: 0.714285714285714rem;\n}\n.font-large-2 {\n  font-size: 1.285714285714286rem;\n}\n.font-small-3 {\n  font-size: 0.571428571428571rem;\n}\n.font-large-3 {\n  font-size: 1.428571428571429rem;\n}\n.font-small-4 {\n  font-size: 0.428571428571429rem;\n}\n.font-large-4 {\n  font-size: 1.571428571428571rem;\n}\n.Select-Root--3ELEx {\n  display: flex;\n  align-items: center;\n}\n.Select-Root--3ELEx > * + * {\n  margin-left: 0.5rem;\n}\n.Select-Label--31xZ4 {\n  user-select: none;\n  cursor: pointer;\n  outline: none;\n}\n.Select-Input--1ePhy {\n  flex: 1 1 0;\n}\n", '' ]]);
const { RSL_COMPONENT } = require("!../../node_modules/@pinto/react-style-loader/react-style-loader.runtime.compiled.js");
const __Root = RSL_COMPONENT('Root', 'SelectRoot', 'Select-Root--3ELEx');
 const Root = __Root
const __Label = RSL_COMPONENT('Label', 'SelectLabel', 'Select-Label--31xZ4');
 const Label = __Label
const __Input = RSL_COMPONENT('Input', 'SelectInput', 'Select-Input--1ePhy');
 const Input = __Input

